import { getP } from 'redux-polyglot'
import api from '../libs/api'
import {
  getCartItems,
  getCartReference,
  isReady as isCartReady
} from '../selectors/cart'
import { getSelectedPackageId } from '../selectors/packages'
import { loadCart } from './cart'

export const initAddons = (preFocusedId) => (dispatch, getState) => {
  if (isCartReady(getState())) {
    const selectedPackageId = getSelectedPackageId(getState())
    if (selectedPackageId > 0) {
      return dispatch(loadPackageAddons(selectedPackageId, preFocusedId))
    } else {
      return dispatch(loadUserAddons(preFocusedId))
    }
  } else {
    return dispatch(loadCart())
      .then(() => {
        const selectedPackageId = getSelectedPackageId(getState())
        if (selectedPackageId > 0) {
          return dispatch(loadPackageAddons(selectedPackageId, preFocusedId))
        } else {
          return dispatch(loadUserAddons(preFocusedId))
        }
      })
  }
}

/**
 * Actions for retrieving package addons
 */
export const FETCH_PACKAGE_ADDONS_REQUEST = 'ADDONS/FETCH_PACKAGE_ADDONS_REQUEST'

const fetchPackageAddonsRequest = id => ({
  type: FETCH_PACKAGE_ADDONS_REQUEST,
  payload: {
    id
  }
})

export const loadPackageAddons = (id, preFocusedId) => dispatch => {
  dispatch(fetchPackageAddonsRequest(id))
  return dispatch(loadPackageDeviceAddons(id))
    .then(() => dispatch(loadPremiumAddons(preFocusedId)))
}

/**
 * Actions for retrieving premium addons
 */
export const FETCH_PREMIUM_ADDONS_SUCCESS = 'ADDONS/FETCH_PREMIUM_ADDONS_SUCCESS'
export const FETCH_PREMIUM_ADDONS_FAILURE = 'ADDONS/FETCH_PREMIUM_ADDONS_FAILURE'

const fetchPremiumAddonsSuccess = (channels, cartItems, preFocusedId) => {
  let addons = []
  let focusedChannelId = -1
  channels.forEach((channel) => {
    addons.push({
      id: channel.id,
      name: channel.name,
      description: '',
      price: channel.price,
      recurring: channel.recurring,
      recurDetails: {
        length: channel.recurring_length,
        type: channel.recurring_type
      },
      hasAccess: channel.has_access,
      isSelected: cartItems.find(item => item.itemId === channel.id && item.itemType === "premium") !== undefined,
      type: channel.type === 'flixfling_channel' ? 'flixfling' : 'channel',
      itemType: 'premium',
      detailsRequested: false
    })
    if (channel.id.toString() === preFocusedId) {
      focusedChannelId = channel.id
    }
  })
  return {
    type: FETCH_PREMIUM_ADDONS_SUCCESS,
    payload: {
      addons,
      focusedChannelId
    }
  }
}

const fetchPremiumAddonsFailure = (error) => ({
  type: FETCH_PREMIUM_ADDONS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

const loadPremiumAddons = (preFocusedId) => (dispatch, getState) => {
  return api.getPremiumAddons()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchPremiumAddonsSuccess(response['premium-channels'], getCartItems(getState()), preFocusedId))
        } else {
          return dispatch(fetchPremiumAddonsFailure(response.reason))
        }
      },
      () => dispatch(fetchPremiumAddonsFailure(fetchPremiumAddonsFailure(getP(getState()).t("checkout_app.error_loading_premium_addons", { _: "An error has occurred while loading premium add-ons." }))))
    )
}

/**
 * Actions for retrieving device addons
 */

export const FETCH_DEVICE_ADDONS_SUCCESS = 'ADDONS/FETCH_DEVICE_ADDONS_SUCCESS'
export const FETCH_DEVICE_ADDONS_FAILURE = 'ADDONS/FETCH_DEVICE_ADDONS_FAILURE'

const fetchDeviceAddonsSuccess = (deviceAddons, cartItems) => {
  let addons = []
  deviceAddons.forEach((deviceAddon) => {
    addons.push({
      id: deviceAddon.id,
      description: '',
      name: deviceAddon.name,
      price: deviceAddon.devices_fee,
      hasAccess: deviceAddon.has_subscription,
      isSelected: cartItems.find(item => item.itemId === deviceAddon.id && item.itemType === "device") !== undefined,
      type: 'device',
      itemType: 'device',
      noOfDevices: deviceAddon.devices_number,
      detailsRequested: true
    })
  })
  return {
    type: FETCH_DEVICE_ADDONS_SUCCESS,
    payload: {
      addons
    }
  }
}

const fetchDeviceAddonsFailure = (error) => ({
  type: FETCH_DEVICE_ADDONS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

const enhanceDeviceAddons = (devices, t) => devices.map(device => {
  return {
    ...device,
    name: t("checkout_app.device_addon_title", {
      _: "%{smart_count} Player Add-on |||| Any %{smart_count} Players Add-on",
      smart_count: device.devices_number
    })
  }
})

const loadPackageDeviceAddons = id => (dispatch, getState) => {
  return api.getPackageDeviceAddons(id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchDeviceAddonsSuccess(enhanceDeviceAddons(response['addons'], getP(getState()).t), getCartItems(getState())))
        } else if (response.status === 403) {
          return dispatch(fetchDeviceAddonsSuccess(enhanceDeviceAddons([], getP(getState()).t), getCartItems(getState())))
        } else {
          return dispatch(fetchDeviceAddonsFailure(response.reason))
        }
      },
      () => dispatch(fetchDeviceAddonsFailure(getP(getState()).t("checkout_app.error_loading_device_addons", { _: "An error has occurred while loading device add-ons." })))
    )
}

/**
 * Actions for retrieving the list of user addons
 */
export const FETCH_USER_ADDONS_REQUEST = 'ADDONS/FETCH_USER_ADDONS_REQUEST'

const fetchUserAddonsRequest = () => ({
  type: FETCH_USER_ADDONS_REQUEST,
})

const loadUserDeviceAddons = () => (dispatch, getState) => {
  return api.getUserDeviceAddons()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchDeviceAddonsSuccess(enhanceDeviceAddons(response['addons'], getP(getState()).t), getCartItems(getState())))
        } else if (response.status === 403) {
          return dispatch(fetchDeviceAddonsSuccess(enhanceDeviceAddons([], getP(getState()).t), getCartItems(getState())))
        } else {
          return dispatch(fetchDeviceAddonsFailure(response.reason))
        }
      },
      () => dispatch(fetchDeviceAddonsFailure(getP(getState()).t("checkout_app.error_loading_device_addons", { _: "An error has occurred while loading device add-ons." })))
    )
}

export const loadUserAddons = (preFocusedId) => dispatch => {
  dispatch(fetchUserAddonsRequest())
  return dispatch(loadUserDeviceAddons())
    .then(() => dispatch(loadPremiumAddons(preFocusedId)))
}

/**
 * Actions for fetching details of a particular add-on
 */
export const FETCH_ADDON_REQUEST = 'ADDONS/FETCH_ADDON_REQUEST'

const fetchAddonRequest = (addonId) => ({
  type: FETCH_ADDON_REQUEST,
  payload: {
    id: addonId
  }
})

export const loadAddon = addonId => dispatch => {
  dispatch(fetchAddonRequest(addonId))
  return Promise.all([
    dispatch(loadAddonDetails(addonId)),
    dispatch(loadAddonVideos(addonId))
  ])
}

/**
 * Actions for retrieving addon details
 */
export const FETCH_ADDON_DETAILS_REQUEST = 'ADDONS/FETCH_ADDON_DETAILS_REQUEST'
export const FETCH_ADDON_DETAILS_SUCCESS = 'ADDONS/FETCH_ADDON_DETAILS_SUCCESS'
export const FETCH_ADDON_DETAILS_FAILURE = 'ADDONS/FETCH_ADDON_DETAILS_FAILURE'

const fetchAddonDetailsRequest = (addonId) => ({
  type: FETCH_ADDON_DETAILS_REQUEST,
  payload: {
    id: addonId
  }
})

const fetchAddonDetailsSuccess = addon => {
  return {
    type: FETCH_ADDON_DETAILS_SUCCESS,
    payload: addon
  }
}

const fetchAddonDetailsFailure = (id, error) => ({
  type: FETCH_ADDON_DETAILS_FAILURE,
  payload: {
    id: id,
    error: error instanceof Error ? error : new Error(error)
  },
  error: true
})

export const loadAddonDetails = addonId => (dispatch, getState) => {
  dispatch(fetchAddonDetailsRequest(addonId))
  return api.getAddonDetails(addonId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchAddonDetailsSuccess(response['premium-channel']))
        } else {
          return dispatch(fetchAddonDetailsFailure(addonId, response.reason))
        }
      },
      () => dispatch(fetchAddonDetailsFailure(addonId, getP(getState()).t("checkout_app.error_loading_addon_details", { _: "An error has occurred while loading add-on details." })))
    )
}

/**
 * Actions for retrieving addon videos
 */
export const FETCH_ADDON_VIDEOS_REQUEST = 'ADDONS/FETCH_ADDON_VIDEOS_REQUEST'
export const FETCH_ADDON_VIDEOS_SUCCESS = 'ADDONS/FETCH_ADDON_VIDEOS_SUCCESS'
export const FETCH_ADDON_VIDEOS_FAILURE = 'ADDONS/FETCH_ADDON_VIDEOS_FAILURE'

const fetchAddonVideosRequest = (addonId) => ({
  type: FETCH_ADDON_VIDEOS_REQUEST,
  payload: {
    id: addonId
  }
})

const fetchAddonVideosSuccess = (id, videos) => {
  let items = []
  videos.forEach((video) => {
    items.push({
      id: video.id,
      name: video.name,
      thumb: video.img.small
    })
  })
  return {
    type: FETCH_ADDON_VIDEOS_SUCCESS,
    payload: {
      id: id,
      items: items
    }
  }
}

const fetchAddonVideosFailure = (id, error) => ({
  type: FETCH_ADDON_VIDEOS_FAILURE,
  payload: {
    id: id,
    error: error instanceof Error ? error : new Error(error)
  },
  error: true
})

export const loadAddonVideos = addonId => dispatch => {
  dispatch(fetchAddonVideosRequest(addonId))
  return api.getAddonVideos(addonId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchAddonVideosSuccess(addonId, response.videos))
        } else {
          return dispatch(fetchAddonVideosFailure(addonId, response.reason))
        }
      },
      () => dispatch(fetchAddonVideosFailure(addonId, getP(getState()).t("checkout_app.error_loading_addon_videos", { _: "An error has occurred while loading add-on videos." })))
    )
}

/**
 * Actions for selecting a package
 */
export const SELECT_ADDON_REQUEST = 'ADDONS/SELECT_ADDON_REQUEST'
export const SELECT_ADDON_SUCCESS = 'ADDONS/SELECT_ADDON_SUCCESS'
export const SELECT_ADDON_FAILURE = 'ADDONS/SELECT_ADDON_FAILURE'

const selectAddonRequest = addon => ({
  type: SELECT_ADDON_REQUEST,
  payload: {
    addon
  }
})

const selectAddonSuccess = (addon, response) => {
  return {
    type: SELECT_ADDON_SUCCESS,
    payload: {
      addon,
      response
    }
  }
}

const selectAddonFailure = error => ({
  type: SELECT_ADDON_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true
})

export const selectAddon = (addon, triggerDispatch = true) => (dispatch, getState) => {
  triggerDispatch && dispatch(selectAddonRequest(addon))
  const cartReference = getCartReference(getState())
  const type = addon.type === "device" ? "addon_device" : "premium"
  return api.addItemToCart(cartReference, addon.id, type)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return triggerDispatch ? dispatch(selectAddonSuccess(addon, response.data)) : Promise.resolve()
        } else {
          return triggerDispatch ? dispatch(selectAddonFailure(response.reason)) : Promise.resolve()
        }
      },
      () => triggerDispatch ? dispatch(selectAddonFailure(getP(getState()).t("checkout_app.error_adding_addon", { _: "An error has occurred while adding the add-on." }))) : Promise.resolve()
    )
}

/**
 * Actions for focusing inside the add-on list
 */
export const FOCUS_ADDON = 'ADDONS/FOCUS_ADDON'

export const focusAddon = (position) => ({
  type: FOCUS_ADDON,
  payload: {
    position
  }
})

/**
 * Actions for focusing the details screen
 */
export const FOCUS_DETAILS = 'ADDONS/FOCUS_DETAILS'

export const focusDetails = (id) => ({
  type: FOCUS_DETAILS
})

/**
 * Actions for dismissing the error screen
 */
export const DISMISS_ERROR = 'ADDONS/DISMISS_ERROR'

export const dismissError = () => ({
  type: DISMISS_ERROR
})

/**
 * Actions for dealing with the Paypal subscription error
 */
const SHOW_PAYPAL_SUBSCRIPTION_ERROR = 'PACKAGES/SHOW_PAYPAL_SUBSCRIPTION_ERROR'
const DISMISS_PAYPAL_SUBSCRIPTION_ERROR = 'PACKAGES/DIMISS_PAYPAL_SUBSCRIPTION_ERROR'

export const showPaypalSubscriptionError = () => ({
  type: SHOW_PAYPAL_SUBSCRIPTION_ERROR
})

export const dismissPaypalSubscriptionError = () => ({
  type: DISMISS_PAYPAL_SUBSCRIPTION_ERROR
})

/**
 * Actions for adding an addon to cart from any page
 */
export const RESET = 'CHECKOUT/ADDONS/RESET'
export const selectAddonAndClear = addon => dispatch => {
  return dispatch(selectAddon(addon, false)).then(dispatch({ type: RESET }))
}